'use client';

import { useEffect, useState, useCallback } from 'react';

export type FaqNavItem = {
  readonly id: string;
  readonly label: string;
  readonly count: number;
};

/**
 * Sticky pillar nav for /faq with scroll-spy.
 *
 * Watches each topic group section via IntersectionObserver and applies a
 * cyan-glow active state to the link whose section is currently in view.
 * Clicking a link smooth-scrolls to the matching section.
 */
export function FaqPillarNav({ items }: { items: readonly FaqNavItem[] }) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;

    const visible = new Map<string, number>();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        }
        // Pick the section with the highest intersection ratio.
        let best: string | null = null;
        let bestRatio = -1;
        for (const [id, ratio] of visible) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = id;
          }
        }
        if (best) setActiveId(best);
      },
      {
        // Trigger when the section enters the upper half of the viewport.
        rootMargin: '-96px 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    for (const el of sections) obs.observe(el);
    return () => obs.disconnect();
  }, [items]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (!el) return;
      const prefersReduced =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      });
      setActiveId(id);
      // Move focus to the heading for screen-reader continuity.
      const heading = el.querySelector<HTMLElement>('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    },
    [],
  );

  return (
    <nav
      className="v4-faq-nav"
      aria-label="FAQ topics"
    >
      <ul className="v4-faq-nav__list">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="v4-faq-nav__link"
              data-active={activeId === item.id ? 'true' : 'false'}
              aria-current={activeId === item.id ? 'true' : undefined}
              onClick={(e) => handleClick(e, item.id)}
            >
              <span>{item.label}</span>
              <span className="v4-faq-nav__count" aria-hidden="true">
                {String(item.count).padStart(2, '0')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
